import React, { useCallback, useEffect, useState } from 'react';

import { Modal, Spin } from 'antd-v5';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'scouting-ui/lib';

import {
  useDeleteMBFileMutation,
  useGetImportFileStatusQuery,
  useStartMBFileProcessingMutation,
} from '@modules/councilUnits/api';
import {
  COUNCIL_IMPORT_FILE_POLL_INTERVAL,
  importFileStatus,
} from '@modules/councilUnits/constants';
import { DetailItem } from '@shared/components';
import {
  isMobileSel,
  organizationGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import { closeMBRModalVisible } from '../../duck/actions';
import { isVisibleMBRModalSel } from '../../duck/selectors';
import AddMBFile from '../MBRUploadForm/MBRUploadForm';
import styles from './MBRUploadModal.less';

const MBRUploadModal = () => {
  const dispatch = useDispatch();
  const isMobile = useSelector(isMobileSel);
  const isVisible = useSelector(isVisibleMBRModalSel);
  const orgGUID = useSelector(organizationGuidSel);
  const [pollingInterval, setPollingInterval] = useState(
    COUNCIL_IMPORT_FILE_POLL_INTERVAL,
  );
  const {
    isFetching: fetchingImportFile,
    isLoading: loadingImportFile,
    data: statusData,
    isError,
  } = useGetImportFileStatusQuery(
    { orgGUID },
    { skip: !orgGUID || !isVisible, pollingInterval },
  );

  const processStatus = statusData?.processStatus || '';
  const uploadId = statusData?.id || 0;

  const summarydata = statusData?.summaryReport
    ? JSON.parse(statusData.summaryReport)
    : '';
  const stopPolling =
    processStatus == importFileStatus.PROCESSING_DONE || isError;

  const [processFileMB, { isLoading: isloadingStartMB }] =
    useStartMBFileProcessingMutation();

  const [deleteFileMB, { isLoading: isloadingDeleteMB }] =
    useDeleteMBFileMutation();

  useEffect(() => {
    if (stopPolling) setPollingInterval(0);
  }, [stopPolling]);

  const loading =
    loadingImportFile ||
    fetchingImportFile ||
    isloadingStartMB ||
    isloadingDeleteMB;

  const handleProcessMBFile = async () => {
    try {
      await processFileMB({
        orgGUID,
        importFileID: uploadId,
      }).unwrap();

      toastService.success(
        <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.processMessage" />,
      );
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }
  };

  const handleDeleteMBFile = async () => {
    try {
      await deleteFileMB({
        orgGUID,
        importFileID: uploadId,
      }).unwrap();

      toastService.success(
        <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.deleteMessage" />,
      );
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }
  };

  const renderDisplay = () => {
    switch (processStatus) {
      case importFileStatus.IN_QUEUE:
        return (
          <div>
            <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.UploadMBR.InQueue"></FormattedHTMLMessage>
            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleDeleteMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
            </Button>
          </div>
        );
      case importFileStatus.VALIDATING:
        return (
          <div>
            <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.Validating"></FormattedMessage>
            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleDeleteMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
            </Button>
          </div>
        );
      case importFileStatus.VALIDATION_DONE:
        return (
          <div>
            <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.ValidationDone"></FormattedMessage>
            {summarydata.UploadDate && (
              <DetailItem
                className={styles.detailItem}
                label={
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.UploadDate" />
                }
              >
                {summarydata.UploadDate}
              </DetailItem>
            )}
            {summarydata.CouncilAdmin && (
              <DetailItem
                className={styles.detailItem}
                label={
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.CouncilAdmin" />
                }
              >
                {summarydata.CouncilAdmin}
              </DetailItem>
            )}
            {summarydata.RowCount && (
              <DetailItem
                className={styles.detailItem}
                label={
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.RowCount" />
                }
              >
                {summarydata.RowCount}
              </DetailItem>
            )}
            {summarydata.RowWithErrors && (
              <DetailItem
                className={styles.detailItem}
                label={
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.RowWithErrors" />
                }
              >
                {summarydata.RowWithErrors}
              </DetailItem>
            )}
            {summarydata.RowForProcess && (
              <DetailItem
                className={styles.detailItem}
                label={
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.RowForProcess" />
                }
              >
                {summarydata.RowForProcess}
              </DetailItem>
            )}
            <br />

            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleProcessMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.ProcessFile" />
            </Button>
            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleDeleteMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
            </Button>
          </div>
        );
      case importFileStatus.IN_PROCESSING_QUEUE:
        return (
          <div>
            <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.UploadMBR.InProcessingQueue"></FormattedHTMLMessage>
            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleDeleteMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
            </Button>
          </div>
        );
      case importFileStatus.PROCESSING:
        return (
          <div>
            <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.Processing"></FormattedMessage>
            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleDeleteMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
            </Button>
          </div>
        );
      case importFileStatus.PROCESSING_DONE:
        return (
          <div>
            <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.ProcessingDone"></FormattedMessage>
            <Button
              size="small"
              color="scouting-red"
              fitText
              ghost
              className={styles.actionButton}
              onClick={handleDeleteMBFile}
            >
              <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.Finish" />
            </Button>
          </div>
        );
      default:
        return <AddMBFile />;
    }
  };

  const handleCloseModal = useCallback(() => {
    dispatch(closeMBRModalVisible());
  }, [dispatch]);
  return (
    <Modal
      mask={!isMobile}
      size="small"
      destroyOnClose
      centered
      headerColor="scouting-warm-gray"
      visible={isVisible}
      title={intl.formatMessage({
        id: 'councilUnits.CouncilUnitsPage.UploadMBR',
      })}
      backTitle={intl.formatMessage({
        id: 'councilUnits.CouncilUnitsPage.UploadMBR',
      })}
      onBack={handleCloseModal}
      onCancel={handleCloseModal}
      footer={null}
    >
      <Spin spinning={loading} size="large" delay={200}>
        {renderDisplay()}
      </Spin>
    </Modal>
  );
};

MBRUploadModal.propTypes = {
  organizationGUID: PropTypes.string,
};

export default MBRUploadModal;
